import { useState } from 'react';
import { CheckIcon, CopyIcon } from '@phosphor-icons/react';

import { PageHeader } from '@/shared/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/Card';
import { Button } from '@/ui/button/Button';
import { Input } from '@/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/Select';
import { useAuth } from '@/contexts/auth-context';
import { useTeams } from '@/api-models/teams';
import {
  type CreateAgentTokenResponse,
  createAgentToken,
  revokeAgentToken,
  useAgentTokens,
} from '@/api-models/agent-tokens';

const TOKEN_TTL_SECONDS = 7 * 24 * 60 * 60;

export const GetAgentTokenPage = () => {
  const { me } = useAuth();
  const { teams } = useTeams();
  const { tokens, mutate: mutateTokens } = useAgentTokens();

  const [teamId, setTeamId] = useState<string | undefined>(me.personalTeamId);
  const [isCreating, setIsCreating] = useState(false);
  const [minted, setMinted] = useState<CreateAgentTokenResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    setIsCreating(true);
    setError(null);
    try {
      const result = await createAgentToken({ teamId, ttlSeconds: TOKEN_TTL_SECONDS });
      setMinted(result);
      setCopied(false);
      mutateTokens();
    } catch {
      setError('Failed to create token. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!minted) return;
    await navigator.clipboard.writeText(minted.token);
    setCopied(true);
  };

  const handleRevoke = async (id: string) => {
    await revokeAgentToken(id);
    mutateTokens();
  };

  return (
    <div className="min-h-screen bg-sunken py-12 px-4">
      <PageHeader title="Get agent token" />

      <div className="mx-auto flex max-w-lg flex-col gap-6">
        <Card radius="xl" padding="none">
          <CardHeader>
            <CardTitle>Get a coding-agent token</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <p className="text-sm text-fg-secondary">
              Signed in as {me.email}. Pick a team and mint a token your coding agent can use to call the Zooid API on
              your behalf. Tokens expire after 7 days and can be revoked below at any time.
            </p>

            <div>
              <label className="mb-1.5 block text-xs font-medium text-fg-tertiary">Team</label>
              <Select value={teamId} onValueChange={setTeamId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a team" />
                </SelectTrigger>
                <SelectContent>
                  {teams.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              variant="primary"
              size={9}
              fullWidth
              onClick={handleCreate}
              isLoading={isCreating}
              disabled={!teamId}
            >
              Get token
            </Button>

            {error && <p className="text-sm text-danger">{error}</p>}

            {minted && (
              <div className="flex flex-col gap-2 rounded-lg border border-line bg-surface-3 p-3">
                <p className="text-xs text-fg-tertiary">
                  Copy this token now — it won&apos;t be shown again. Expires{' '}
                  {new Date(minted.expiresAt).toLocaleString()}.
                </p>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={minted.token}
                    className="font-mono text-xs"
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <Button variant="secondary" size={9} shape="square" onClick={handleCopy} title="Copy to clipboard">
                    {copied ? <CheckIcon size={16} /> : <CopyIcon size={16} />}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {tokens.length > 0 && (
          <Card radius="xl" padding="none">
            <CardHeader>
              <CardTitle>Active tokens</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              {tokens.map((token) => (
                <div key={token.id} className="flex items-center justify-between gap-2 text-sm">
                  <div className="flex flex-col">
                    <span className="font-mono text-fg">{token.tokenPrefix}…</span>
                    <span className="text-xs text-fg-tertiary">
                      Expires {new Date(token.expiresAt).toLocaleString()}
                    </span>
                  </div>
                  <Button variant="ghost" size={7} onClick={() => handleRevoke(token.id)}>
                    Revoke
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
